import React, { ReactElement, useState, useMemo, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useLocation } from 'react-router-dom';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { connect, useSelector } from 'react-redux';
import { HeaderWrapper, Title2, Description } from './styledElements';
import { DashlineSeparator } from '../join/styledElements';
import CompletePhotoUploadForm from './components/CompletePhotoUploadForm';
import JoinActionButton from '../join/JoinActionButton';
import JoinSkipButton from '../join/JoinSkipButton';
import CompleteProfileDetailsForm from './components/CompleteProfileDetailsForm';
import CompleteAboutMeForm from './components/CompleteAboutMeForm';
import JoinSkipConfirmation from '../join/JoinSkipConfirmation';
import useModal from '../../hooks/useModal';
import { useApp } from '../../contexts/AppContext';
import _isEmpty from 'lodash/isEmpty';
import { updateProfileMandatoryCompletion } from '../../actions/profileActions';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { handleProfileCompletionVysionEvent } from '../../utils/vysion/profileCompletion';
import { VYSION_ACTIONS } from '../../utils/vysion';
import { ProfileCompletionClickVysionEventsEnum } from '../../utils/vysion/profileCompletion/click';
import Optional from '../../atoms/Optional';
import uuidv4 from 'uuid';
import { showUploadSnackbar } from '../../actions/uploadSnackbarActions';
import { galleryBulkUploadMedia } from '../../actions/galleryActions';
import { baseurltoFile } from '../../modules/gallery/helpers';
import { MAX_NUMBER_PHOTOS } from '../../config/Master';
import {
  FIRSTNAME_FIRST_LETTER_VALIDATOR,
  FIRSTNAME_VALIDATOR,
  OCCUPATION_MAX_CHARACTER,
} from '../../config/constants';
import { ICompleteProfileFormData, ICompleteProfileWallProps } from './types/CompleteProfileWall';
import { convertVhToPx } from '../../utils/helpers';
import { MODAL_BODY_PADDING_VALUE, MODAL_MAX_HEIGHT_VALUE } from '../../constants/modal';
import breakpoints from '../../theme/material/breakpoints';
import { FileType } from '../../modules/gallery/constants';
import { mixpanelTrackEvent } from '../../utils/mixpanel/common';
import { RootState } from '../../store';

const ScrollbarsContentView = styled('div')`
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`;

const Wrapper = styled('div')({
  padding: 12,
  paddingLeft: 60,
  paddingRight: 60,
  paddingBottom: 40,
  // to replace later with new media query from Responsive UI
  [breakpoints.down('sm')]: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: 12,
  },
});

const StyledFormContainer = styled('div')({
  position: 'relative',
});

const StyledFormOverlayDisabled = styled('div')({
  position: 'absolute',
  height: '100%',
  width: '100%',
  top: 0,
  backround: 'transparent',
  zIndex: 1,
});

const convertToFormData = async (data: ICompleteProfileFormData) => {
  const formDataObj = new FormData();
  const promises = [];

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined || (key === 'photos' && value.length === 0)) {
      continue;
    }

    if (key === 'photos') {
      const promise = baseurltoFile(value[0].edited).then(file => {
        formDataObj.append('photos[]', file);
      });

      promises.push(promise);
    } else {
      formDataObj.append(key, value === null ? '' : (value as string));
    }
  }

  await Promise.all(promises);

  return formDataObj;
};

const CompleteProfileWall = ({
  hasRequiredPhoto,
  shouldRequiredUsername,
  profileData,
  updateMandatoryCompletion,
  action,
  profile,
  showSnackbar,
  uploadPhotos,
}: ICompleteProfileWallProps): ReactElement => {
  const location = useLocation();
  const { resetModal } = useModal();

  const {
    setModalState,
    profileCompletionCallback,
    setProfileCompletionCallback,
    profileCompletionCallbackAfterValidate,
  } = useApp();

  const [showAgreementModal, setShowAgreementModal] = useState<boolean>(false);
  const [formData, setFormData] = useState<ICompleteProfileFormData>({
    body_type: null,
    children: null,
    smoking: null,
    drinking: null,
    education: null,
    income: null,
    height: null,
    occupation: null,
    net_worth: null,
    description: '',
    seeking: '',
    username: '',
  });
  const [dynamicAutoMaxHeight, setDynamicAutoMaxHeight] = useState<number>(
    convertVhToPx(MODAL_MAX_HEIGHT_VALUE) - MODAL_BODY_PADDING_VALUE
  );

  const photosRef = useRef<any>([]);
  const [photos, setPhotos] = useState([]);

  const showRequiredPhoto = useMemo(() => hasRequiredPhoto, [hasRequiredPhoto]);
  const isRequiredUsername = useMemo(() => shouldRequiredUsername, [shouldRequiredUsername]);
  const username = useSelector((state: RootState) => state.profile.username);

  useEffect(() => {
    const {
      height,
      body_types,
      children,
      smoking,
      drinking,
      education,
      income,
      net_worth,
      description,
      seeking,
      occupation,
    } = profileData;

    const bodyType = body_types.data.length > 0 ? parseInt(body_types.data[0].value) : null;

    handleFormData({
      body_type: bodyType,
      children: children === 0 ? null : children,
      smoking: smoking === 0 ? null : smoking,
      drinking: drinking === 0 ? null : drinking,
      education: education === 0 ? null : education,
      income: income === 0 ? null : income,
      net_worth: net_worth === 0 ? null : net_worth,
      height,
      occupation,
      description,
      seeking,
      username: isRequiredUsername ? '' : undefined,
    });
  }, [profileData]);

  const allowSubmit = useMemo(() => {
    const hasUploadedPhoto = showRequiredPhoto
      ? photos.length > 0 && photos.length <= MAX_NUMBER_PHOTOS
        ? true
        : false
      : true;

    let validDescriptionOrSeeking = true;

    const validateDesc = !formData.description ? true : formData.description.length >= 50;
    const validateSeek = !formData.seeking ? true : formData.seeking.length >= 50;

    validDescriptionOrSeeking = validateDesc && validateSeek;

    let isValidOccupation =
      formData.occupation === null ||
      (formData.occupation !== null &&
        formData.occupation !== undefined &&
        formData.occupation?.length <= OCCUPATION_MAX_CHARACTER);

    if (!formData.occupation && profileData?.occupation?.length > OCCUPATION_MAX_CHARACTER) {
      isValidOccupation = false;
    }

    const incompleteFields = [
      formData.body_type,
      formData.height,
      formData.children,
      formData.smoking,
      formData.drinking,
      formData.education,
      formData.height,
      ...(profileData?.account_type === 'Generous' ? [formData.income, formData.net_worth] : []),
    ];

    const updateIncompleteFields = incompleteFields.filter(
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      field => field === '' || field === null || field === undefined
    );

    let isValidUsername = true;

    if (isRequiredUsername) {
      const isValidStartLetter = FIRSTNAME_FIRST_LETTER_VALIDATOR.test(formData.username);
      const isValidLetter = formData?.username?.match(FIRSTNAME_VALIDATOR);

      if (
        formData.username.length < 3 ||
        formData.username.length > 16 ||
        (!isValidLetter && !isValidStartLetter) ||
        username?.toLowerCase() === formData.username?.toLowerCase()
      ) {
        isValidUsername = false;
      }
    }

    return Boolean(
      updateIncompleteFields.length === 0 &&
        hasUploadedPhoto &&
        validDescriptionOrSeeking &&
        isValidUsername &&
        isValidOccupation
    );
  }, [formData, showRequiredPhoto, profileData, isRequiredUsername, photos]);

  const handleFormData = (state: Partial<ICompleteProfileFormData>) => {
    setFormData(prev => {
      return {
        ...prev,
        ...state,
      };
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const updatedFormData = await convertToFormData({ ...formData, photos });

    updateMandatoryCompletion(updatedFormData, async () => {
      // close mandatory popup
      resetModal();

      const clonedPhotos = [...photos];

      clonedPhotos.splice(0, 1);

      for (const photo of clonedPhotos) {
        showSnackbar({
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          id: uuidv4(),
          status: '',
          type: 'photo',
          file: photo,
        });
      }

      // start upload process
      uploadPhotos();

      // add mixpanel profile wall step with photos
      mixpanelTrackEvent({
        eventName: EVENT_NAMES.PROFILE_WALL_STEP_2_COMPLETED,
        payload: {
          eventProperties: {
            '# of photos added': photos.length,
            Result: 'Completed',
            Action: action,
          },
        },
      });

      // launch this callback pass from the last component action
      if (profileCompletionCallback && typeof profileCompletionCallback === 'function') {
        profileCompletionCallback();
      }

      // reset all state in mandatory popup with .8 delay
      setTimeout(() => {
        setProfileCompletionCallback(() => undefined);
      }, 800);
    });

    handleProfileCompletionVysionEvent(
      {
        action: VYSION_ACTIONS.CLICK,
        event: ProfileCompletionClickVysionEventsEnum.CLICK_FINISH_PROFILE_SAVE_AND_FINISH,
      },
      location.pathname
    );
  };

  const onSkip = () => {
    setProfileCompletionCallback(() => undefined);
    profileCompletionCallbackAfterValidate();
    resetModal();

    mixpanelTrackEvent({
      eventName: EVENT_NAMES.PROFILE_WALL_STEP_2_COMPLETED,
      payload: {
        eventProperties: {
          '# of photos added': photos.length,
          Result: 'Skipped',
          Action: action,
        },
      },
    });

    handleProfileCompletionVysionEvent(
      {
        action: VYSION_ACTIONS.CLICK,
        event: ProfileCompletionClickVysionEventsEnum.CLICK_FINISH_PROFILE_SKIP_FOR_NOW,
      },
      location.pathname
    );
  };

  const handleModalOverlay = (state: React.CSSProperties) => {
    setModalState(prev => {
      return {
        ...prev,
        customOverlayStyle: state,
      };
    });
  };

  const updatePhotos = (newPhotos: FileType[]) => {
    photosRef.current = [...newPhotos];
    setPhotos(photosRef.current);
  };

  const handleOnClickSkip = () => {
    if (profile.isProcessing) return;

    handleModalOverlay({ display: 'none' });
    setShowAgreementModal(true);
  };

  const showRequiredProfileDetailLists = useMemo(() => {
    const {
      height,
      body_types,
      children,
      smoking,
      drinking,
      education,
      income,
      net_worth,
      description,
      seeking,
      occupation,
      account_type,
    } = profileData;

    return {
      username: isRequiredUsername,
      body_type: body_types.data.length === 0,
      children: children === 0,
      smoking: smoking === 0,
      drinking: drinking === 0,
      education: education === 0,
      height: _isEmpty(height),
      income: account_type === 'Attractive' ? false : income === 0,
      net_worth: account_type === 'Attractive' ? false : net_worth === 0,
      occupation: _isEmpty(occupation) || occupation?.length > OCCUPATION_MAX_CHARACTER,
      description: _isEmpty(description) || (!_isEmpty(description) && description.length < 50),
      seeking: _isEmpty(seeking) || (!_isEmpty(seeking) && seeking.length < 50),
    };
  }, [profileData, isRequiredUsername]);

  const memoizedCompletePhotoUploadForm = useMemo(
    () =>
      showRequiredPhoto && (
        <CompletePhotoUploadForm
          label={`Upload at least 1 public photo`}
          onCancelUpload={() => handleModalOverlay({ display: 'block' })}
          handlePhotoUpload={updatePhotos}
          previewPhotos={photos}
        />
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showRequiredPhoto, photos]
  );

  const memoizedCompleteDetailsForm = useMemo(
    () => (
      <CompleteProfileDetailsForm
        showRequiredProfileDetailLists={showRequiredProfileDetailLists}
        formData={formData}
        handleFormData={handleFormData}
        gender={profileData.gender}
        country={profileData.country}
        occupation={profileData.occupation}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showRequiredProfileDetailLists, formData, profileData.gender, profileData.country, profileData.occupation]
  );

  useEffect(() => {
    const handleResize = () => {
      setDynamicAutoMaxHeight(convertVhToPx(MODAL_MAX_HEIGHT_VALUE) - MODAL_BODY_PADDING_VALUE);
    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Scrollbars
      autoHeight
      autoHeightMin={100}
      autoHeightMax={dynamicAutoMaxHeight}
      style={{ marginTop: 10 }}
      renderView={props => <ScrollbarsContentView {...props} />}
    >
      <Wrapper>
        <HeaderWrapper style={{ marginBottom: 20 }}>
          <Title2>{`Let's Finish Up Your Profile`}</Title2>
          <Description>
            Completed profiles allow you to make and accept WYP Date Offers, and add members to your favorites.
          </Description>
        </HeaderWrapper>
        <DashlineSeparator />
        <StyledFormContainer>
          {profile.isProcessing && <StyledFormOverlayDisabled />}
          {memoizedCompletePhotoUploadForm}
          {memoizedCompleteDetailsForm}
          <Optional show={true}>
            <CompleteAboutMeForm
              showRequiredProfileDetailLists={showRequiredProfileDetailLists}
              formData={formData}
              handleFormData={handleFormData}
            />
          </Optional>
        </StyledFormContainer>
        <div style={{ textAlign: 'center' }}>
          <JoinActionButton
            label={profile.isProcessing ? 'Processing ' : 'Save and Finish'}
            customStyle={{ marginBottom: 10, padding: '15px 22px' }}
            isDisabled={!allowSubmit || profile.isProcessing}
            onClick={handleSubmit}
          />
          <JoinSkipButton label="Skip for now" onClick={handleOnClickSkip} />
          <JoinSkipConfirmation
            isOpen={showAgreementModal}
            alwaysDisabledBodyScroll={true}
            onGoBack={() => {
              setShowAgreementModal(false);
              handleModalOverlay({ display: 'block' });
            }}
            onClose={() => {
              setShowAgreementModal(false);
              handleModalOverlay({ display: 'block' });
            }}
            onSkip={onSkip}
            customStyleSkipButton={{ paddingBottom: 20 }}
          />
        </div>
      </Wrapper>
    </Scrollbars>
  );
};

const mapStateToProps = ({ profile, common }) => {
  const profileCompletion = profile.mandatory_profile_status;

  return {
    profile,
    profileData: profile && profile.profile?.data,
    hasRequiredPhoto: profileCompletion.fields_missed.includes('Photos'),
    shouldRequiredUsername: profileCompletion.fields_missed.includes('Username'),
    errors: common.errors,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updateMandatoryCompletion(formData, callback) {
      return dispatch(updateProfileMandatoryCompletion(formData, callback));
    },
    showSnackbar: payload => {
      dispatch(showUploadSnackbar(payload));
    },
    uploadPhotos: () => {
      dispatch(galleryBulkUploadMedia('photo'));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CompleteProfileWall);
